/**
 * Segment utility functions for the Segment Editor
 */

/**
 * Calculate effective duration of a segment considering trim and speed
 * @param {Object} segment - The segment object
 * @returns {number} - Effective duration in milliseconds
 */
export const getEffectiveDuration = (segment) => {
  if (!segment) return 0;
  const baseDuration = (segment.duration_ms || 5000) - (segment.trim_start_ms || 0) - (segment.trim_end_ms || 0);
  const speed = segment.speed ?? 1;
  return baseDuration / speed;
};

/**
 * Get raw segment duration (without speed adjustment) after trimming
 * @param {Object} segment - The segment object
 * @returns {number} - Raw duration in milliseconds
 */
export const getRawDuration = (segment) => {
  if (!segment) return 5000;
  return (segment.duration_ms || 5000) - (segment.trim_start_ms || 0) - (segment.trim_end_ms || 0);
};

/**
 * Calculate start_ms for all segments based on order (seamless, no gaps)
 * @param {Array} segments - Array of segments
 * @returns {Array} - Segments with calculated start_ms
 */
export const calculateSegmentPositions = (segments) => {
  if (!segments?.length) return [];
  
  // Sort by order
  const sorted = [...segments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  
  let currentPosition = 0;
  return sorted.map((segment, index) => {
    const newSegment = {
      ...segment,
      order: index,
      start_ms: currentPosition
    };
    currentPosition += getEffectiveDuration(newSegment);
    return newSegment;
  });
};

/**
 * Format milliseconds to timecode (MM:SS;FF at 24fps)
 * @param {number} ms - Time in milliseconds
 * @returns {string} - Formatted timecode
 */
export const formatTimecode = (ms) => {
  if (!ms && ms !== 0) return '00:00;00';
  const absMs = Math.max(0, ms);
  const totalSeconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const frames = Math.floor((absMs % 1000) / (1000 / 24));
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')};${String(frames).padStart(2, '0')}`;
};

/**
 * Format duration in seconds with one decimal
 * @param {number} ms - Duration in milliseconds
 * @returns {string} - Formatted duration
 */
export const formatDuration = (ms) => {
  if (!ms) return '0.0s';
  return `${(ms / 1000).toFixed(1)}s`;
};
